import React, { useState, useEffect, useRef } from 'react';
import { api, examApi } from '../services/api';
import ReportModal from '../components/ReportModal';
import './ExamPage.css';

interface ExamPageProps {
  sessionId: string;
  onExit: () => void;
}

interface Question {
  question_id: string;
  question_number: number;
  total_questions: number;
  question_text: string;
  options: Array<{ option_number: number; option_text: string; }>;
  expected_answers_count: number;
  time_remaining_minutes: number;
  exam_duration_minutes?: number;
  is_answered: boolean;
  is_flagged?: boolean;
}

interface QuestionStatus {
  questionNumber: number;
  isAnswered: boolean;
  selectedAnswers: number[];
  isFlagged?: boolean;
}

interface AnswerResult {
  isCorrect: boolean;
  correctAnswers?: number[];
  explanation?: string;
  reasoning?: string;
}

interface ConfirmModalState {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  destructive?: boolean;
}

const INACTIVITY_WARNING_MS = 180000;
const INACTIVITY_PAUSE_MS = 300000;

const ExamPage: React.FC<ExamPageProps> = ({ sessionId, onExit }) => {
  const [question, setQuestion] = useState<Question | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isPaused, setIsPaused] = useState(false);
  const [isPauseBusy, setIsPauseBusy] = useState(false);
  const [serverTimeRemaining, setServerTimeRemaining] = useState<number | null>(null);
  const [timeSyncedAt, setTimeSyncedAt] = useState<Date | null>(null);
  const [questionStatuses, setQuestionStatuses] = useState<QuestionStatus[]>([]);
  const [showNavigation, setShowNavigation] = useState(false);
  const [answerResult, setAnswerResult] = useState<AnswerResult | null>(null);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isAutoSaved, setIsAutoSaved] = useState(false);
  const [examType, setExamType] = useState<string>(''); // 시험 타입 추가
  const [examDuration, setExamDuration] = useState<number>(130); // 시험 시간 (분)
  const [infoModal, setInfoModal] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: '',
    message: ''
  });
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    open: false,
    title: '',
    message: '',
    confirmText: '확인',
    cancelText: '취소',
    destructive: false
  });
  const confirmActionRef = useRef<(() => Promise<void> | void) | null>(null);
  const inactivityWarningTimerRef = useRef<number | null>(null);
  const inactivityPauseTimerRef = useRef<number | null>(null);
  const lastActivityTouchRef = useRef<number>(0);
  const inactivityWarningOpenRef = useRef(false);

  const openInfoModal = (title: string, message: string) => {
    setInfoModal({ open: true, title, message });
  };

  const openConfirmModal = (
    config: Omit<ConfirmModalState, 'open'>,
    onConfirm: () => Promise<void> | void
  ) => {
    confirmActionRef.current = onConfirm;
    setConfirmModal({ open: true, ...config });
  };

  const loadQuestion = async (questionNumber: number) => {
    setIsLoading(true);
    setError(null);
    setAnswerResult(null); // 새 문제 로드 시 이전 결과 초기화
    setIsAutoSaved(false); // 자동 저장 상태 초기화
    try {
      const response = await examApi.getQuestion(sessionId, questionNumber);
      if (response.success) {
        const questionData = response.data as Question;
        setQuestion(questionData);
        setCurrentQuestionNumber(questionNumber);
        if (typeof questionData.time_remaining_minutes === 'number') {
          setServerTimeRemaining(questionData.time_remaining_minutes);
          setTimeSyncedAt(new Date());
        }
        if (typeof questionData.exam_duration_minutes === 'number') {
          setExamDuration(questionData.exam_duration_minutes);
        }
        
        // Update flag status
        if (questionData.is_flagged) {
          setFlaggedQuestions(prev => new Set(prev).add(questionNumber));
        }
      } else {
        setError('문제를 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Error loading question:', err);
      setError(`문제를 불러오는 중 오류가 발생했습니다: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextQuestion = () => {
    setAnswerResult(null);
    if (currentQuestionNumber < question!.total_questions) {
      loadQuestion(currentQuestionNumber + 1);
    }
  };

  const handleAnswerSelect = async (optionNumber: number) => {
    console.log('옵션 클릭됨:', optionNumber);
    if (!question) return;
    if (examType === 'practice' && answerResult) return;

    let newSelectedAnswers: number[];

    if (question.expected_answers_count === 1) {
      newSelectedAnswers = [optionNumber];
    } else {
      if (selectedAnswers.includes(optionNumber)) {
        newSelectedAnswers = selectedAnswers.filter(num => num !== optionNumber);
      } else if (selectedAnswers.length < question.expected_answers_count) {
        newSelectedAnswers = [...selectedAnswers, optionNumber];
      } else {
        return;
      }
    }

    setSelectedAnswers(newSelectedAnswers);
    console.log('답안 설정됨:', newSelectedAnswers);

    // 문제 상태 업데이트
    setQuestionStatuses(prev => {
      const filtered = prev.filter(s => s.questionNumber !== currentQuestionNumber);
      return [...filtered, {
        questionNumber: currentQuestionNumber,
        isAnswered: newSelectedAnswers.length > 0,
        selectedAnswers: newSelectedAnswers
      }];
    });

    // 무조건 자동 저장
    if (newSelectedAnswers.length > 0) {
      console.log('자동 저장 시작...');
      try {
        const response = await examApi.submitAnswer(sessionId, currentQuestionNumber, newSelectedAnswers);
        console.log('서버 응답:', response);
        if (response.success) {
          setIsAutoSaved(true);
          console.log('✅ 답안이 자동으로 저장되었습니다!');
          
          // 연습모드인 경우에만 정답과 해설 표시 (완성된 답안일 때만)
          if (examType === 'practice' && newSelectedAnswers.length === question.expected_answers_count) {
            const result = response.data as any;
            if (result.correctAnswers) {
              setAnswerResult({
                isCorrect: result.isCorrect,
                correctAnswers: result.correctAnswers,
                explanation: result.explanation,
                reasoning: result.reasoning
              });
              setIsPracticeMode(true);
              return;
            }
          }
        } else {
          console.log('❌ 서버에서 실패 응답:', response);
        }
      } catch (err) {
        console.error('❌ 자동 저장 실패:', err);
        setIsAutoSaved(false);
      }
    } else {
      console.log('답안이 없어서 자동 저장 안함');
      setIsAutoSaved(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!question || selectedAnswers.length === 0) return;

    try {
      const response = await examApi.submitAnswer(sessionId, currentQuestionNumber, selectedAnswers);
      if (response.success) {
        const result = response.data as any;
        
        // 연습모드인 경우 정답과 해설 표시
        if (examType === 'practice' && result.correctAnswers) {
          setAnswerResult({
            isCorrect: result.isCorrect,
            correctAnswers: result.correctAnswers,
            explanation: result.explanation,
            reasoning: result.reasoning
          });
          setIsPracticeMode(true);
          return; // 연습모드에서는 자동으로 다음 문제로 넘어가지 않음
        }
        
        // 문제 상태를 제출됨으로 업데이트
        setQuestionStatuses(prev => {
          const filtered = prev.filter(s => s.questionNumber !== currentQuestionNumber);
          return [...filtered, {
            questionNumber: currentQuestionNumber,
            isAnswered: true,
            selectedAnswers: selectedAnswers
          }];
        });

        setIsAutoSaved(true);
        console.log('답안이 저장되었습니다.');
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('답안 제출 중 오류가 발생했습니다.');
    }
  };

  const handleCompleteExam = async () => {
    try {
      await examApi.submit(sessionId);
      onExit();
    } catch (err: any) {
      if (err.response?.data?.error?.code === 'NO_ANSWERS_PROVIDED') {
        openConfirmModal(
          {
            title: '답변 없음',
            message: '문제를 한 문제도 풀지 않아 기록에 남지 않습니다. 세션을 삭제하고 종료할까요?',
            confirmText: '삭제 후 종료',
            cancelText: '계속 시험',
            destructive: true
          },
          () => onExit()
        );
      } else if (err.response?.data?.error?.code === 'INSUFFICIENT_ANSWERS') {
        openConfirmModal(
          {
            title: '답변 수 부족',
            message: '10문제 미만으로 답변한 시험은 기록에 남지 않습니다. 세션을 삭제하고 종료할까요?',
            confirmText: '삭제 후 종료',
            cancelText: '계속 시험',
            destructive: true
          },
          () => onExit()
        );
      } else {
        setError('시험 완료 중 오류가 발생했습니다.');
      }
    }
  };

  const handleStartReview = () => {
    // 모든 문제에 답안이 있는지 확인
    const unansweredQuestions = questionStatuses.filter(status => !status.isAnswered);
    
    if (unansweredQuestions.length > 0) {
      const unansweredNumbers = unansweredQuestions.map(q => q.questionNumber).join(', ');
      openInfoModal('미답변 문항', `다음 문제들에 답안을 입력해주세요: ${unansweredNumbers}번`);
      return;
    }
    
    setIsReviewMode(true);
    setShowNavigation(true);
  };

  const handleFinishReview = async () => {
    try {
      await examApi.submit(sessionId);
      onExit();
    } catch (err: any) {
      if (err.response?.data?.error?.code === 'NO_ANSWERS_PROVIDED') {
        openConfirmModal(
          {
            title: '답변 없음',
            message: '문제를 한 문제도 풀지 않아 기록에 남지 않습니다. 세션을 삭제하고 종료할까요?',
            confirmText: '삭제 후 종료',
            cancelText: '계속 검토',
            destructive: true
          },
          () => onExit()
        );
      } else if (err.response?.data?.error?.code === 'INSUFFICIENT_ANSWERS') {
        openConfirmModal(
          {
            title: '답변 수 부족',
            message: '10문제 미만으로 답변한 시험은 기록에 남지 않습니다. 세션을 삭제하고 종료할까요?',
            confirmText: '삭제 후 종료',
            cancelText: '계속 검토',
            destructive: true
          },
          () => onExit()
        );
      } else {
        setError('시험 완료 중 오류가 발생했습니다.');
      }
    }
  };

  const handleToggleFlag = async () => {
    if (!question || isPracticeMode) return;
    
    try {
      const response = await examApi.toggleFlag(sessionId, currentQuestionNumber);
      if (response.success) {
        const isFlagged = (response.data as any).isFlagged;
        setFlaggedQuestions(prev => {
          const newSet = new Set(prev);
          if (isFlagged) {
            newSet.add(currentQuestionNumber);
          } else {
            newSet.delete(currentQuestionNumber);
          }
          return newSet;
        });
        
        // Update question status
        setQuestionStatuses(prev => 
          prev.map(status => 
            status.questionNumber === currentQuestionNumber 
              ? { ...status, isFlagged }
              : status
          )
        );
      }
    } catch (err) {
      console.error('Error toggling flag:', err);
    }
  };

  const performAbandonExam = async () => {
    try {
      await examApi.abandon(sessionId);
    } catch (error) {
      console.error('Error abandoning exam:', error);
    } finally {
      onExit();
    }
  };

  const syncProgress = async (touchActivity = true) => {
    const progressResponse = await examApi.getProgress(sessionId);
    if (!progressResponse.success) return null;

    const sessionData = progressResponse.data as any;
    setIsPaused(sessionData.status === 'paused');

    if (typeof sessionData.timeRemainingMinutes === 'number') {
      setServerTimeRemaining(sessionData.timeRemainingMinutes);
      setTimeSyncedAt(new Date());
    }

    if (sessionData.exam_duration_minutes) {
      setExamDuration(sessionData.exam_duration_minutes);
    }

    return touchActivity ? sessionData : sessionData;
  };

  const handlePauseExam = async () => {
    if (isPauseBusy || isPaused) return;

    setIsPauseBusy(true);
    try {
      const response = await examApi.pause(sessionId);
      if (response.success) {
        const data = response.data as any;
        setIsPaused(true);
        if (typeof data.timeRemainingMinutes === 'number') {
          setServerTimeRemaining(data.timeRemainingMinutes);
          setTimeSyncedAt(new Date());
        }
      }
    } catch (err) {
      console.error('Error pausing exam:', err);
    } finally {
      setIsPauseBusy(false);
    }
  };

  const handleResumeExam = async () => {
    if (isPauseBusy || !isPaused) return;

    setIsPauseBusy(true);
    try {
      const response = await examApi.resume(sessionId);
      if (response.success) {
        const data = response.data as any;
        setIsPaused(false);
        if (typeof data.timeRemainingMinutes === 'number') {
          setServerTimeRemaining(data.timeRemainingMinutes);
          setTimeSyncedAt(new Date());
        }
        await loadQuestion(currentQuestionNumber);
      }
    } catch (err) {
      console.error('Error resuming exam:', err);
    } finally {
      setIsPauseBusy(false);
    }
  };

  const touchExamActivity = async () => {
    if (isPaused) return;

    const now = Date.now();
    if (now - lastActivityTouchRef.current < 30000) return;
    lastActivityTouchRef.current = now;

    try {
      const response = await examApi.touchActivity(sessionId);
      if (response.success && typeof (response.data as any)?.timeRemainingMinutes === 'number') {
        setServerTimeRemaining((response.data as any).timeRemainingMinutes);
        setTimeSyncedAt(new Date());
      }
    } catch (err) {
      console.error('Exam activity touch failed:', err);
    }
  };

  const clearInactivityTimers = () => {
    if (inactivityWarningTimerRef.current) {
      window.clearTimeout(inactivityWarningTimerRef.current);
      inactivityWarningTimerRef.current = null;
    }

    if (inactivityPauseTimerRef.current) {
      window.clearTimeout(inactivityPauseTimerRef.current);
      inactivityPauseTimerRef.current = null;
    }

    inactivityWarningOpenRef.current = false;
  };

  const resetInactivityTimers = () => {
    clearInactivityTimers();
    if (isPaused) return;

    inactivityWarningTimerRef.current = window.setTimeout(() => {
      inactivityWarningOpenRef.current = true;
      openConfirmModal(
        {
          title: '응답이 없습니다',
          message: '문제 풀이 시간이 길어지고 있습니다. 2분 뒤 시험이 자동으로 일시정지됩니다. 계속 응시하시겠습니까?',
          confirmText: '계속 응시',
          cancelText: '일시정지',
          destructive: false
        },
        async () => {
          await touchExamActivity();
          resetInactivityTimers();
        }
      );
    }, INACTIVITY_WARNING_MS);

    inactivityPauseTimerRef.current = window.setTimeout(async () => {
      setConfirmModal(prev => ({ ...prev, open: false }));
      await handlePauseExam();
    }, INACTIVITY_PAUSE_MS);
  };

  const registerExamActivity = (event: Event) => {
    if (inactivityWarningOpenRef.current && event.type === 'mouseover') {
      return;
    }

    if (isPaused) return;
    if (inactivityWarningOpenRef.current) {
      setConfirmModal(prev => ({ ...prev, open: false }));
      inactivityWarningOpenRef.current = false;
    }
    void touchExamActivity();
    resetInactivityTimers();
  };

  const handleAbandonExam = () => {
    const answeredCount = questionStatuses.filter(s => s.isAnswered).length;
    
    let message = '시험을 포기하시겠습니까?';
    if (answeredCount === 0) {
      message += '\n\n문제를 한 문제도 풀지 않았으므로 기록에 남지 않습니다.';
    } else if (answeredCount < 10) {
      message += `\n\n${answeredCount}문제만 답변하여 10문제 미만이므로 기록에 남지 않습니다.`;
    } else {
      message += `\n\n지금까지 ${answeredCount}문제를 풀었지만 시험은 미완료 상태로 남습니다.`;
    }
    
    openConfirmModal(
      {
        title: '시험 포기',
        message,
        confirmText: '포기하기',
        cancelText: '계속 풀기',
        destructive: true
      },
      performAbandonExam
    );
  };


  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const getRealTimeRemaining = () => {
    if (serverTimeRemaining !== null && timeSyncedAt) {
      if (isPaused) return serverTimeRemaining;

      const elapsedSinceSync = Math.floor((currentTime.getTime() - timeSyncedAt.getTime()) / (1000 * 60));
      return Math.max(0, serverTimeRemaining - elapsedSinceSync);
    }

    if (!startTime || !question) return 0;
    const elapsed = Math.floor((currentTime.getTime() - startTime.getTime()) / (1000 * 60));
    return Math.max(0, examDuration - elapsed);
  };

  const getTimeProgress = () => {
    if (!question) return 0;
    const remaining = getRealTimeRemaining();
    return ((examDuration - remaining) / examDuration) * 100;
  };

  useEffect(() => {
    const initializeExam = async () => {
      try {
        // 진행상황 조회
        const sessionData = await syncProgress();
        if (sessionData) {
          
          // 시험 모드 설정 (mode 필드 사용)
          const examModeFromServer = sessionData.mode || sessionData.examType || 'practice';
          setExamType(examModeFromServer);
          
          // 시험 시간 설정 (분 단위)
          if (sessionData.examDuration) {
            setExamDuration(Math.floor(sessionData.examDuration / 60));
          } else if (sessionData.exam_duration_minutes) {
            setExamDuration(sessionData.exam_duration_minutes);
          }
          
          // paused 상태이면 active로 전환
          if (sessionData.status === 'paused') {
            console.log('Resuming paused session...');
            const resumeResponse = await examApi.resume(sessionId);
            console.log('Resume response:', resumeResponse);
            setIsPaused(false);
            if (resumeResponse.success && typeof (resumeResponse.data as any)?.timeRemainingMinutes === 'number') {
              setServerTimeRemaining((resumeResponse.data as any).timeRemainingMinutes);
              setTimeSyncedAt(new Date());
            }
          }
          
          const currentQ = sessionData.currentQuestion;
          const totalQuestions = sessionData.totalQuestions;
          setStartTime(new Date(sessionData.startedAt));

          // 문제 상태 초기화 - 세션 데이터에서 답변 상태 복원
          const initialStatuses: QuestionStatus[] = [];
          for (let i = 1; i <= totalQuestions; i++) {
            const sessionQuestion = sessionData.questions?.find((q: any) => q.question_order === i);
            const hasAnswer = sessionQuestion?.answered_at && sessionQuestion?.user_answers?.length > 0;
            
            initialStatuses.push({
              questionNumber: i,
              isAnswered: hasAnswer || false,
              selectedAnswers: sessionQuestion?.user_answers || [],
              isFlagged: sessionQuestion?.is_flagged || false
            });
          }
          setQuestionStatuses(initialStatuses);

          // 플래그된 문제들 초기화
          const flaggedSet = new Set<number>();
          sessionData.questions?.forEach((q: any) => {
            if (q.is_flagged) {
              flaggedSet.add(q.question_order);
            }
          });
          setFlaggedQuestions(flaggedSet);
          
          // 초기 문제 로드
          const targetQuestion = currentQ > totalQuestions ? totalQuestions : currentQ;
          setCurrentQuestionNumber(targetQuestion);
          loadQuestion(targetQuestion);
        } else {
          // 기본값으로 첫 번째 문제 로드
          setCurrentQuestionNumber(1);
          loadQuestion(1);
        }
      } catch (err) {
        console.error('Error initializing exam:', err);
        setCurrentQuestionNumber(1);
        loadQuestion(1);
      }
    };
    
    initializeExam();
  }, [sessionId]);

  useEffect(() => {
    // 1초마다 시간 업데이트
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const heartbeat = setInterval(() => {
      syncProgress().catch((err) => {
        console.error('Exam heartbeat failed:', err);
      });
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [sessionId, isPaused]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncProgress().catch((err) => {
          console.error('Exam visibility sync failed:', err);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sessionId]);

  useEffect(() => {
    if (isPaused) {
      clearInactivityTimers();
      return;
    }

    const events: Array<keyof WindowEventMap> = ['click', 'keydown', 'touchstart', 'mouseover', 'wheel'];
    events.forEach((eventName) => window.addEventListener(eventName, registerExamActivity, { passive: true }));
    resetInactivityTimers();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, registerExamActivity));
      clearInactivityTimers();
    };
  }, [sessionId, isPaused]);

  // questionStatuses가 업데이트될 때 현재 문제의 답안을 복원하고 자동 저장 상태 확인
  useEffect(() => {
    if (questionStatuses.length > 0 && currentQuestionNumber > 0) {
      const existingStatus = questionStatuses.find(s => s.questionNumber === currentQuestionNumber);
      if (existingStatus) {
        setSelectedAnswers(existingStatus.selectedAnswers);
        // 답안이 있으면 자동 저장된 상태로 간주
        setIsAutoSaved(existingStatus.selectedAnswers.length > 0);
      } else {
        setIsAutoSaved(false);
      }
    }
  }, [questionStatuses, currentQuestionNumber]);

  if (isLoading) {
    return (
      <div className="exam-page">
        <div className="loading">문제를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="exam-page">
        <div className="error">{error}</div>
        <button onClick={onExit}>돌아가기</button>
      </div>
    );
  }

  if (!question) return null;

  return (
    <div className="exam-page">
      <header className="exam-shell-header">
        <div className="exam-shell-header__left">
          <span className="exam-shell-header__label">QUESTION</span>
          <strong>{question.question_number} / {question.total_questions}</strong>
        </div>
        <div className="exam-shell-header__center">
          <span>남은 시간 {formatTime(getRealTimeRemaining())}</span>
          <div className="time-progress-bar">
            <div
              className="time-progress-fill"
              style={{ width: `${getTimeProgress()}%` }}
            />
          </div>
        </div>
        <div className="exam-shell-header__right">
          <button
            className={`navigation-button ${showNavigation ? 'active' : ''}`}
            onClick={() => setShowNavigation(!showNavigation)}
          >
            {showNavigation ? '목록 닫기' : '문제 목록'}
          </button>
          {!isPracticeMode && (
            <button
              className={`flag-button ${flaggedQuestions.has(currentQuestionNumber) ? 'flagged' : ''}`}
              onClick={handleToggleFlag}
              disabled={isPaused}
            >
              {flaggedQuestions.has(currentQuestionNumber) ? '🚩 플래그됨' : '🏳️ 플래그'}
            </button>
          )}
          <button
            className={`pause-button ${isPaused ? 'paused' : ''}`}
            onClick={isPaused ? handleResumeExam : handlePauseExam}
            disabled={isPauseBusy}
          >
            {isPaused ? '시험 재개' : '일시정지'}
          </button>
          <button className="abandon-button" onClick={handleAbandonExam}>시험 포기</button>
        </div>
      </header>

      {showNavigation && <div className="question-navigation-backdrop" onClick={() => setShowNavigation(false)} />}
      <aside className={`question-navigation-drawer ${showNavigation ? 'open' : ''}`}>
        <div className="navigation-header">
          <h3>{isReviewMode ? '시험 검토' : '문제 목록'}</h3>
          <button
            className="close-navigation"
            onClick={() => setShowNavigation(false)}
          >
            ×
          </button>
        </div>
        {!isPracticeMode && (
          <div className="navigation-filters">
            <button
              className={`filter-button ${showFlaggedOnly ? 'active' : ''}`}
              onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
            >
              {showFlaggedOnly ? '전체 보기' : '플래그만'}
            </button>
            <span className="flagged-count">플래그 {flaggedQuestions.size}</span>
          </div>
        )}
        <div className="navigation-grid">
          {questionStatuses
            .filter(status => !showFlaggedOnly || flaggedQuestions.has(status.questionNumber))
            .map((status) => (
            <button
              key={status.questionNumber}
              className={`navigation-item ${status.questionNumber === currentQuestionNumber ? 'current' : ''
                } ${status.isAnswered ? 'answered' : ''} ${flaggedQuestions.has(status.questionNumber) ? 'flagged' : ''}`}
              onClick={() => {
                loadQuestion(status.questionNumber);
                setShowNavigation(false);
              }}
            >
              {status.questionNumber}
              {flaggedQuestions.has(status.questionNumber) && <span className="flag-indicator">🚩</span>}
            </button>
          ))}
        </div>
      </aside>

      <main className="exam-main">
        {isPaused ? (
          <div className="exam-paused-panel">
            <span className="exam-paused-panel__eyebrow">일시정지됨</span>
            <h2>시험 시간이 멈춰 있습니다</h2>
            <p>재개 버튼을 누르면 남은 시간 계산이 다시 시작됩니다.</p>
            <button onClick={handleResumeExam} disabled={isPauseBusy}>
              시험 재개하기
            </button>
          </div>
        ) : (
          <div className="question-section">
            <div className="question-header">
              <h2>문제 {question.question_number}</h2>
              <button className="report-button-inline" onClick={() => setShowReportModal(true)}>
                🚨 문제 신고
              </button>
            </div>
            <div className="question-text">{question.question_text}</div>

            <div className="answer-instruction">
              {question.expected_answers_count === 1
                ? '다음 중 하나를 선택하세요.'
                : `다음 중 ${question.expected_answers_count}개를 선택하세요.`
              }
            </div>

            <div className="options">
              {question.options.map((option) => (
                <div
                  key={option.option_number}
                  className={`option ${selectedAnswers.includes(option.option_number) ? 'selected' : ''} ${examType === 'practice' && answerResult ? 'locked' : ''}`}
                  onClick={() => handleAnswerSelect(option.option_number)}
                  aria-disabled={examType === 'practice' && !!answerResult}
                >
                  <div className="option-number">{option.option_number}</div>
                  <div className="option-text">{option.option_text}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {answerResult && (
          <div className="practice-result">
            <div className={`result-indicator ${answerResult.isCorrect ? 'correct' : 'incorrect'}`}>
              {answerResult.isCorrect ? '✓ 정답입니다!' : '✗ 오답입니다.'}
            </div>
            <div className="correct-answers">
              <strong>정답: </strong>
              {answerResult.correctAnswers?.join(', ')}
            </div>
            {answerResult.explanation && (
              <div className="explanation">
                <strong>해설:</strong>
                <p>{answerResult.explanation}</p>
              </div>
            )}
            {answerResult.reasoning && (
              <div className="reasoning">
                <strong>이유:</strong>
                <p>{answerResult.reasoning}</p>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="exam-footer-actions">
        <div className="exam-footer-actions__left">
          <span>{selectedAnswers.length}개 선택됨</span>
          {isAutoSaved && <span className="autosaved-chip">자동 저장됨</span>}
        </div>
        <div className="exam-footer-actions__right">
          {currentQuestionNumber > 1 && (
            <button
              className="prev-button"
              onClick={() => loadQuestion(currentQuestionNumber - 1)}
              disabled={isPaused}
            >
              ← 이전
            </button>
          )}
          {isReviewMode ? (
            <button
              className="submit-answer-button"
              onClick={handleSubmitAnswer}
              disabled={isPaused || selectedAnswers.length !== question.expected_answers_count}
            >
              답안 수정
            </button>
          ) : (
            <button
              className={`submit-answer-button ${isAutoSaved ? 'auto-saved' : ''}`}
              onClick={handleSubmitAnswer}
              disabled={isPaused || selectedAnswers.length === 0}
            >
              {isAutoSaved ? '저장됨' : '답안 저장'}
            </button>
          )}

          {currentQuestionNumber < question.total_questions && (
            <button
              className="next-button"
              onClick={() => {
                if (answerResult) {
                  handleNextQuestion();
                } else {
                  loadQuestion(currentQuestionNumber + 1);
                }
              }}
              disabled={isPaused}
            >
              다음 →
            </button>
          )}
          {currentQuestionNumber === question.total_questions && (
            <>
              <button
                className="review-button"
                onClick={isReviewMode ? handleFinishReview : handleStartReview}
                disabled={isPaused}
              >
                {isReviewMode ? '시험 완료' : '시험 검토'}
              </button>
              {!isReviewMode && !isPaused && (
                <button className="complete-button" onClick={handleCompleteExam}>바로 제출</button>
              )}
            </>
          )}
        </div>
      </footer>

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        sessionId={sessionId}
        questionNumber={currentQuestionNumber}
        onSubmit={async (reportType, description) => {
          try {
            const response = await api.post('/report/question', {
              sessionId,
              questionNumber: currentQuestionNumber,
              reportType,
              description
            });
            
            if (response.success) {
              openInfoModal('신고 접수 완료', '문제 신고가 정상적으로 접수되었습니다.');
            } else {
              openInfoModal('신고 실패', '신고 접수 중 오류가 발생했습니다.');
            }
          } catch {
            openInfoModal('신고 실패', '신고 접수 중 오류가 발생했습니다.');
          }
        }}
      />

      {infoModal.open && (
        <div className="exam-action-modal-overlay">
          <div className="exam-action-modal">
            <h3>{infoModal.title}</h3>
            <p>{infoModal.message}</p>
            <div className="exam-action-modal-buttons single">
              <button className="modal-btn primary" onClick={() => setInfoModal({ open: false, title: '', message: '' })}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="exam-action-modal-overlay">
          <div className="exam-action-modal">
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="exam-action-modal-buttons">
              <button
                className="modal-btn secondary"
                onClick={() => {
                  confirmActionRef.current = null;
                  setConfirmModal(prev => ({ ...prev, open: false }));
                }}
              >
                {confirmModal.cancelText}
              </button>
              <button
                className={`modal-btn ${confirmModal.destructive ? 'danger' : 'primary'}`}
                onClick={async () => {
                  const action = confirmActionRef.current;
                  confirmActionRef.current = null;
                  setConfirmModal(prev => ({ ...prev, open: false }));
                  if (action) {
                    await action();
                  }
                }}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPage;
